import { MelodyLib, VocalLib, chordTones, intervalName, withArticle } from '@tunasong/music-lib'
import type { Melody, Scale } from '@tunasong/schemas'

/**
 * The phrase picker's rows (Pitch Trainer Phase 2, plan M5 step 5.1; Joni §2 and §9). Pure: the
 * dialog renders them, and every string a row shows that is not lexicon copy is built here, once.
 */

export interface MelodySource {
  kind: 'melody'
  /** Stable for the song: the melody block's id */
  id: string
  section: string
  isChorus: boolean
  melody: Melody
  /** 1-based, only when its section has two or more melodies (D3) */
  melodyIndex?: number
  /** The section's or song's key, for a melody without its own */
  key?: Scale
}

export interface ChartSource {
  kind: 'chart'
  id: string
  section: string
  isChorus: boolean
  lines: VocalLib.ChartSection['lines']
}

export type PracticeSource = MelodySource | ChartSource

export interface PickerTag {
  lexiconId: string
  tokens: Record<string, string | number>
}

interface PickerItemBase {
  id: string
  section: string
  /** "Chorus · bars 9–12", "Guide tones · Verse, line 2" */
  label: string
  /** The Hear it button's accessible name */
  hearName: string
  /** The row's description, for the `aria-hidden` thumbnail: "8 notes, D4 to B4", "4 chords" */
  textAlternative: string
  /** What the thumbnail draws and Hear it plays */
  thumbnail: Melody
  tags: PickerTag[]
  isSuggested: boolean
}

export interface PhraseItem extends PickerItemBase {
  kind: 'phrase'
  source: MelodySource
  phrase: MelodyLib.Phrase
  level: MelodyLib.PhraseLevel
  /** Other places the same phrase occurs: "Also in Chorus 2" */
  /** Where else the same material is, as the row shows it after "Also in"; null when nowhere */
  alsoIn: string | null
  /** Warn early, don't hide: such a row stays selectable and leads to the refusal screen (Joni §4) */
  isWiderThanRange: boolean
}

export interface PartItem extends PickerItemBase {
  kind: 'guide-tones' | 'outline'
  source: ChartSource
  part: VocalLib.ChordPart
  /** Joni §9: on every surface, the one place "melody" appears, to deny it */
  subLabel: string
  /** Joni, #2458: every guide-tone part in this section would stay on one note, so it offers outlines only */
  isOutlineOnly: boolean
  /** Where else the same material is, as the row shows it after "Also in"; null when nowhere */
  alsoIn: string | null
  level?: undefined
  isWiderThanRange: false
}

export type PickerItem = PhraseItem | PartItem

const noteName = (midi: number, spelled: string) => `${spelled}${Math.floor(midi / 12) - 1}`

const barsLabel = ({ startBar, endBar }: { startBar: number; endBar: number }) =>
  startBar === endBar ? `bar ${startBar}` : `bars ${startBar}–${endBar}`

const linesLabel = ({ first, last }: VocalLib.ChordPart['lines']) =>
  first === last ? `line ${first}` : `lines ${first}–${last}`

const subLabels: Record<PartItem['kind'], string> = {
  'guide-tones': 'One note per chord — not the song’s melody.',
  outline: 'Up and down each chord — not the song’s melody.',
}

const reasonTags = (phrase: MelodyLib.Phrase): PickerTag[] =>
  phrase.reasons.map((reason): PickerTag => {
    switch (reason.kind) {
      case 'leap':
        return {
          lexiconId: 'vocal-difficulty-leap',
          tokens: { interval: intervalName(reason.semitones), aInterval: withArticle(intervalName(reason.semitones)) },
        }
      case 'chromatic':
        return { lexiconId: 'vocal-difficulty-chromatic', tokens: { count: reason.count } }
      case 'span':
        return {
          lexiconId: 'vocal-difficulty-span',
          tokens: { span: intervalName(reason.semitones), aSpan: withArticle(intervalName(reason.semitones)) },
        }
      case 'fast':
        return { lexiconId: 'vocal-difficulty-fast', tokens: {} }
    }
  })

const phraseRows = (source: MelodySource, range: VocalLib.VocalRange | null) =>
  MelodyLib.extractPhrases(source.melody, {
    sectionName: source.section,
    isChorus: source.isChorus,
    ...(source.melodyIndex ? { melodyIndex: source.melodyIndex } : {}),
    ...(source.key ? { key: source.key } : {}),
  }).map(phrase => ({ source, phrase, range }))

const toPhraseItem = ({ source, phrase, range }: ReturnType<typeof phraseRows>[number], index: number): PhraseItem => {
  const where = `${source.melodyIndex ? `melody ${source.melodyIndex}, ` : ''}${barsLabel(phrase.source)}`
  const fit = VocalLib.fitPhrase(
    phrase.events.map(event => event.midi),
    { range, ...((source.melody.key ?? source.key) ? { key: source.melody.key ?? source.key } : {}) }
  )
  const isWiderThanRange = fit.kind === 'refused'
  const ordered = [...phrase.events].sort((a, b) => a.midi - b.midi)
  const lowest = ordered[0]
  const highest = ordered[ordered.length - 1]
  return {
    kind: 'phrase',
    id: `${source.id}:phrase:${index}`,
    section: source.section,
    label: `${source.section} · ${where}`,
    hearName: `Hear ${source.section}, ${where}`,
    textAlternative: `${phrase.slice.notes.length} notes, ${noteName(lowest.midi, lowest.name)} to ${noteName(highest.midi, highest.name)}`,
    thumbnail: phrase.slice,
    tags: [
      ...reasonTags(phrase),
      ...(isWiderThanRange ? [{ lexiconId: 'vocal-phrase-out-of-range', tokens: {} }] : []),
    ],
    isSuggested: false,
    source,
    phrase,
    level: phrase.level,
    alsoIn: phrase.alsoIn.length > 0 ? phrase.alsoIn.map(origin => origin.sectionName).join(', ') : null,
    isWiderThanRange,
  }
}

/**
 * Where in its line a part sits, when the line alone does not say: "chords 1–7" for a piece of a line too
 * long for one part, and "chords 3–4" for an outline part from a line that gives more than one (Joni, #2515)
 */
const chordPositions = (source: ChartSource, guideTones: VocalLib.ChordPart[], outlines: VocalLib.ChordPart[]) => {
  /** Each sung chord's line and its place in that line, in the order the parts take them */
  const sung = source.lines.flatMap(line =>
    line.chords
      .filter(chord => chordTones(chord))
      .map((_chord, index) => ({ lineNumber: line.lineNumber, position: index + 1 }))
  )
  const range = (from: number, count: number) => `chords ${from}–${from + count - 1}`
  const pieces = new Map<VocalLib.ChordPart, string>()
  const takenInLine = new Map<number, number>()
  for (const part of guideTones.filter(entry => entry.piece)) {
    const taken = takenInLine.get(part.lines.first) ?? 0
    pieces.set(part, range(taken + 1, part.chords.length))
    takenInLine.set(part.lines.first, taken + part.chords.length)
  }
  const outlinesInLine = (lineNumber: number) =>
    outlines.filter(entry => entry.lines.first === lineNumber && entry.lines.last === lineNumber).length
  let next = 0
  for (const part of outlines) {
    const [first] = sung.slice(next, next + part.chords.length)
    next += part.chords.length
    const isOneLine = part.lines.first === part.lines.last
    if (first && isOneLine && outlinesInLine(part.lines.first) > 1) {
      pieces.set(part, range(first.position, part.chords.length))
    }
  }
  return pieces
}

const partItems = (source: ChartSource, range: VocalLib.VocalRange): PartItem[] => {
  const { guideTones, outlines, skipped } = VocalLib.chartParts({ name: source.section, lines: source.lines }, range)
  const isOutlineOnly = guideTones.length === 0 && skipped.length > 0
  const positions = chordPositions(source, guideTones, outlines)
  const place = (part: VocalLib.ChordPart) => {
    const position = positions.get(part)
    return `${linesLabel(part.lines)}${position ? `, ${position}` : ''}`
  }
  /**
   * Joni, #2515: outline parts with the same chords in order sing the same notes, so a section shows
   * the first one only, saying where else it is: up to two more places, then a count
   */
  const repeatsOf = new Map<VocalLib.ChordPart, VocalLib.ChordPart[]>()
  const kept = outlines.filter(part => {
    const first = [...repeatsOf.keys()].find(candidate => candidate.chords.join(' ') === part.chords.join(' '))
    if (first) {
      repeatsOf.get(first)?.push(part)
      return false
    }
    repeatsOf.set(part, [])
    return true
  })
  const alsoInOf = (part: VocalLib.ChordPart) => {
    const repeats = repeatsOf.get(part) ?? []
    if (repeats.length === 0) {
      return null
    }
    const shown = repeats.slice(0, 2).map(place).join('; ')
    return repeats.length > 2 ? `${shown} and ${repeats.length - 2} more` : shown
  }
  return [...guideTones, ...kept].map((part, index) => {
    const kindName = part.kind === 'guide-tones' ? 'Guide tones' : 'Chord outline'
    /** An outline part is 2 chords, so recognising it by its chords beats its position (Joni, #2515) */
    const chords = part.kind === 'outline' ? ` (${part.chords.join(', ')})` : ''
    const where = `${source.section}, ${place(part)}${chords}`
    return {
      kind: part.kind,
      id: `${source.id}:${part.kind}:${index}`,
      section: source.section,
      label: `${kindName} · ${where}`,
      hearName: `Hear ${kindName.toLowerCase()}, ${where}`,
      textAlternative: `${part.chords.length} ${part.chords.length === 1 ? 'chord' : 'chords'}`,
      thumbnail: part.line.melody,
      tags: [
        { lexiconId: part.kind === 'guide-tones' ? 'vocal-ex-guide-tones' : 'vocal-ex-chord-outline', tokens: {} },
      ],
      isSuggested: false,
      source,
      part,
      subLabel: subLabels[part.kind],
      isOutlineOnly,
      alsoIn: part.kind === 'outline' ? alsoInOf(part) : null,
      isWiderThanRange: false,
    }
  })
}

/**
 * A chord line is built inside the singer's range (M2). Without a measured range, G3–G4 (Bowie, #2515): a
 * whole octave, so it holds every pitch class and any 1-5 outline, centred where every voice type
 * overlaps. A high voice sings it an octave up, which the octave rule counts as right.
 */
const defaultChordRange: VocalLib.VocalRange = { low: 55, high: 67 }

/**
 * Every row the picker offers for these sources, in song order: melody phrases, deduplicated with
 * "Also in", and for chart sections their guide-tone parts and then their outline parts. One row is
 * Suggested: the easiest chorus phrase, else the easiest phrase; with no melody at all, the first
 * chorus guide-tone part, else the first guide-tone part.
 */
export const buildPickerItems = (sources: PracticeSource[], range: VocalLib.VocalRange | null): PickerItem[] => {
  const melodyRows = sources.flatMap(source => (source.kind === 'melody' ? phraseRows(source, range) : []))
  /** First occurrences only, each with `alsoIn` for its repeats; a repeat shares its events array */
  const dedupedPhrases = MelodyLib.dedupePhrases(melodyRows.map(row => row.phrase))
  const suggested = MelodyLib.suggestPhrase(dedupedPhrases)

  const items = sources.flatMap((source): PickerItem[] => {
    if (source.kind === 'chart') {
      return partItems(source, range ?? defaultChordRange)
    }
    return melodyRows
      .filter(row => row.source === source)
      .flatMap((row, index) => {
        const deduped = dedupedPhrases.find(candidate => candidate.events === row.phrase.events)
        if (!deduped) {
          return []
        }
        return [{ ...toPhraseItem({ ...row, phrase: deduped }, index), isSuggested: deduped === suggested }]
      })
  })

  if (items.some(item => item.isSuggested)) {
    return items
  }
  const guideTones = items.filter(item => item.kind === 'guide-tones')
  const pick = guideTones.find(item => item.source.isChorus) ?? guideTones[0]
  return items.map(item => (item === pick ? { ...item, isSuggested: true } : item))
}
